var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Deck_1;
import React from 'react';
import { LayoutGap, SelectionType } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { EditorContext } from '@philips-internal/oswald-ui-core/lib/component/context/index.js';
import { bool, eventListener, formatters, oneOf, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { RenderEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { SelectItemEvent } from '@philips-internal/oswald-ui-core/lib/component/types/selectable-list.js';
import { MessageService, ModelService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, enumValues, isContentfulString, isEmptyObject, isFunction } from '@philips-internal/oswald-ui-utils';
import { BaseCardList } from '../base-card-list/base-card-list.js';
import { DeckAPI } from './deck-api.js';
import { DeckGroupBackgroundColor } from './deck-constants.js';
import DeckContext from './deck-context.js';
import { DeckGroup } from './deck-group/deck-group.js';
import './deck.css';
const DeckContainer = {
    acceptedTypes: [
        ComponentType.BEHAVIOR,
        ComponentType.DATA_DISPLAY,
    ],
    name: '',
};
let Deck = Deck_1 = class Deck extends BaseCardList {
    constructor() {
        super(...arguments);
        this.state = {
            ...this.state,
            selectedGroups: [],
        };
        this.getCardGroupProps = (groupKey, cards, parentGroupKey) => {
            const { collapsible, groupBackgroundColor, groupContentMargin, groupContentPadding, initialCollapsed, selectionStyle = Deck_1.defaultProps.selectionStyle, selectionType = Deck_1.defaultProps.selectionType, subGroupBackgroundColor, subGroupContentMargin, subGroupContentPadding, } = this.props;
            const isSubGroup = isContentfulString(parentGroupKey);
            return {
                collapsible,
                context: this.getContext(),
                groupBackgroundColor: isSubGroup ? subGroupBackgroundColor : groupBackgroundColor,
                groupContentMargin: isSubGroup ? subGroupContentMargin : groupContentMargin,
                groupContentPadding: isSubGroup ? subGroupContentPadding : groupContentPadding,
                groupKey,
                initialCollapsed,
                onSelect: (selected) => this.updateGroupSelection(groupKey, cards, selected),
                renderHeaderContent: () => this.renderGroupHeaderContent(groupKey, cards, isSubGroup),
                selectable: this.isGroupSelectable(),
                selected: this.isGroupSelected(groupKey),
                selectionStyle,
                selectionType,
            };
        };
        this.renderCardGroup = (groupKey, cards, parentGroupKey) => {
            const groupProps = this.getCardGroupProps(groupKey, cards, parentGroupKey);
            const isSubGroup = isContentfulString(parentGroupKey);
            const className = classNames({
                'c-deck__group-grid-item': !isSubGroup,
                'c-deck__sub-group-grid-item': isSubGroup,
            });
            return (React.createElement("div", { className: className, key: groupKey },
                React.createElement(DeckGroup, { ...groupProps }, this.renderCards(cards))));
        };
        this.renderCardGroups = (data, parentGroupKey) => {
            return Object.entries(data).map(([groupKey, cards]) => this.renderCardGroup(groupKey, cards, parentGroupKey));
        };
        this.renderCardGroupWithSubGroup = (groupKey, cards) => {
            const { subGroupBy, subGroupGap } = this.props;
            const groupProps = this.getCardGroupProps(groupKey, cards);
            const subGroupData = this.getGroupedData(cards, subGroupBy);
            const groupsWrapperStyle = {
                rowGap: subGroupGap,
            };
            return (React.createElement("div", { className: "c-deck__group-grid-item", key: groupKey },
                React.createElement(DeckGroup, { ...groupProps },
                    React.createElement("div", { className: "c-deck__sub-group-grid", style: groupsWrapperStyle }, subGroupData ? this.renderCardGroups(subGroupData, groupProps.groupKey) : null))));
        };
        this.renderCardGroupsWithSubGroups = (data) => {
            return Object.entries(data).map(([groupKey, cards]) => this.renderCardGroupWithSubGroup(groupKey, cards));
        };
    }
    setContext() {
        this.setState({ context: new DeckContext() });
    }
    getGroupedData(data, groupBy) {
        if (!data || !Array.isArray(data) || !groupBy) {
            return null;
        }
        const groupedData = data.reduce((accumulator, value) => {
            const groupByFieldValue = String(value[groupBy]);
            if (!groupByFieldValue) {
                return accumulator;
            }
            const oldGroupValue = accumulator[groupByFieldValue] || [];
            oldGroupValue.push(value);
            accumulator[groupByFieldValue] = oldGroupValue;
            return accumulator;
        }, {});
        if (isEmptyObject(groupedData)) {
            return null;
        }
        return groupedData;
    }
    isGroupSelected(groupKey) {
        const { selectedGroups } = this.state;
        if (!this.isGroupSelectable()) {
            return false;
        }
        return this.getContext().isGroupSelected(groupKey, selectedGroups);
    }
    getFilteredSelectedGroups(groupKey, select) {
        const { selectionType = Deck_1.defaultProps.selectionType } = this.props;
        const { selectedGroups } = this.state;
        return this.getContext().modifyGroupSelection({
            item: groupKey,
            remove: !select,
            selection: selectedGroups,
            type: selectionType,
        });
    }
    getFilteredSelectedCards(cards, select) {
        const { selectionType } = this.props;
        const { selectedCards } = this.state;
        const filteredSelectedCards = selectedCards.filter(key => !cards.some(card => this.getCardKey(card) === key));
        if (!select) {
            return filteredSelectedCards;
        }
        const cardsKeys = cards.map(card => this.getCardKey(card));
        return selectionType === SelectionType.SINGLE_GROUP ? cardsKeys : [...filteredSelectedCards, ...cardsKeys];
    }
    updateGroupSelection(groupKey, cards, selected) {
        const { onSelectGroup, onSelectItem, onUnselectGroup, onUnselectItem, selectionType = Deck_1.defaultProps.selectionType, } = this.props;
        if (!this.isGroupSelectable()) {
            return;
        }
        const newSelectedGroups = this.getFilteredSelectedGroups(groupKey, selected);
        const newSelectedCards = this.getFilteredSelectedCards(cards, selected);
        const callback = () => {
            const groupEvent = selected ? onSelectGroup : onUnselectGroup;
            const itemEvent = selected ? onSelectItem : onUnselectItem;
            if (this.getContext().allowGroupSelectionEvent(selectionType)) {
                if (isFunction(groupEvent)) {
                    groupEvent(new SelectItemEvent({ item: cards }));
                }
                return;
            }
            if (selectionType === SelectionType.MULTIPLE) {
                cards.forEach(card => {
                    if (isFunction(itemEvent)) {
                        itemEvent(new SelectItemEvent({ item: card }));
                    }
                });
            }
        };
        this.setState({ selectedCards: newSelectedCards, selectedGroups: newSelectedGroups }, callback);
    }
    onBeforeUpdateCardSelection(event, card) {
        const { groupBy, selectionType } = this.props;
        if (event.selected || SelectionType.MULTIPLE !== selectionType) {
            return;
        }
        this.setState({ selectedGroups: this.getFilteredSelectedGroups(String(card[groupBy]), false) });
    }
    renderGroupHeaderContent(groupKey, cards, isSubGroup = false) {
        const { formatters, onDynamicContentRender } = this.props;
        let renderFn = () => {
            const title = isSubGroup
                ? ModelService.formatProps({ subGroupBy: groupKey }, ['subGroupBy'], formatters).subGroupBy
                : ModelService.formatProps({ groupBy: groupKey }, ['groupBy'], formatters).groupBy;
            return (React.createElement("div", { className: "c-deck__group-header-default-content" },
                React.createElement("div", { className: "c-deck__group-header-title" }, MessageService.translate(title))));
        };
        if (isFunction(onDynamicContentRender)) {
            const render = (fn) => {
                renderFn = fn;
            };
            onDynamicContentRender(new RenderEvent({
                data: cards,
                entry: groupKey,
                render,
                type: 'grouped-deck-header-content',
            }));
        }
        return renderFn();
    }
    isGroupSelectable() {
        const { selectable, selectionType = Deck_1.defaultProps.selectionType } = this.props;
        return this.getContext().allowGroupSelection(selectable, selectionType);
    }
    renderContainer(editorContext) {
        const { children, uuid } = this.props;
        if (!editorContext?.isDesigning()) {
            return null;
        }
        return (React.createElement(Container, { acceptedTypes: DeckContainer.acceptedTypes, className: "u-grid-col-12", repeated: true, uuid: uuid }, children));
    }
    renderContent(editorContext) {
        const { accumulate, data, loading, groupBy, groupGap, subGroupBy } = this.props;
        if (editorContext?.isDesigning()) {
            return this.renderContainer(editorContext);
        }
        if (!data || !Array.isArray(data) || (!accumulate && loading)) {
            return null;
        }
        if (!groupBy) {
            return this.renderCards(data);
        }
        const groupsWrapperStyle = {
            rowGap: groupGap,
        };
        const groupedData = this.getGroupedData(data, groupBy);
        const renderMethod = subGroupBy ? this.renderCardGroupsWithSubGroups : this.renderCardGroups;
        return (React.createElement("div", { className: "c-deck__group-grid", style: groupsWrapperStyle }, groupedData ? renderMethod(groupedData) : null));
    }
    render() {
        const { className, overflow } = this.props;
        const { context } = this.state;
        if (!context || isEmptyObject(context)) {
            return null;
        }
        const classesNames = classNames({
            'c-deck': true,
            'c-deck--overflow u-scrollbar': Boolean(overflow),
            [`${className}`]: Boolean(className),
        });
        return (React.createElement(EditorContext.Consumer, null, editorContext => (React.createElement(DeckContext.Context.Provider, { value: context },
            React.createElement("div", { className: classesNames },
                this.renderNotFound(),
                this.renderContent(editorContext),
                this.renderLoading(),
                this.renderLoadMore())))));
    }
};
Deck.propTypes = {
    ...BaseCardList.propTypes,
    collapsible: bool.group('group'),
    formatters: formatters,
    groupBackgroundColor: oneOf(enumValues(DeckGroupBackgroundColor)).group('group'),
    groupBy: string.group('group'),
    groupContentMargin: string.group('group'),
    groupContentPadding: string.group('group'),
    groupGap: oneOf(enumValues(LayoutGap)).group('group'),
    initialCollapsed: bool.group('group'),
    onClick: eventListener,
    onDynamicContentRender: eventListener,
    onSelectGroup: eventListener,
    onUnselectGroup: eventListener,
    overflow: bool,
    subGroupBackgroundColor: oneOf(enumValues(DeckGroupBackgroundColor)).group('group'),
    subGroupBy: string.group('group'),
    subGroupContentMargin: string.group('group'),
    subGroupContentPadding: string.group('group'),
    subGroupGap: oneOf(enumValues(LayoutGap)).group('group'),
};
Deck.defaultProps = {
    ...BaseCardList.defaultProps,
    collapsible: false,
    groupBackgroundColor: DeckGroupBackgroundColor.DEFAULT,
    groupGap: LayoutGap.SMALL,
    initialCollapsed: false,
    subGroupBackgroundColor: DeckGroupBackgroundColor.TRANSPARENT,
    subGroupGap: LayoutGap.SMALL,
};
Deck = Deck_1 = __decorate([
    AwesomeComponent({ api: DeckAPI, containers: [DeckContainer], type: ComponentType.LIST })
], Deck);
export { Deck, };
//# sourceMappingURL=deck.js.map